import React, { useCallback } from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, cn } from "@lemonadetraining/web-ui";
import { IconArrowRight, IconVideo } from "@tabler/icons-react";
import { fadeInUp, buttonAnimation } from "./AnimationVariants";
import { openDashboard } from "../../layout/openDashboard";

interface RootState {
  user: { isLoggedIn: boolean };
}

const PRIMARY =
  "w-full h-11 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90";

interface SecondaryAction {
  label: string;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
}

/* Create account + Request a Demo for visitors, Go to Dashboard for signed-in users.
   `className` sizes the row: the hero fills its column, the closing section centres a fixed width.
   `secondaryWhenSignedIn` keeps the second button next to Go to Dashboard (the closing section).
   `secondary` replaces Request a Demo, for pages whose visitors don't book demos. */
export function PrimaryActions({
  className,
  secondaryWhenSignedIn = false,
  secondary,
}: {
  className?: string;
  secondaryWhenSignedIn?: boolean;
  secondary?: SecondaryAction;
}) {
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);

  const handleSignUp = useCallback(() => navigate("/register"), [navigate]);
  const handleRequestDemo = useCallback(() => {
    const subject = encodeURIComponent("Request Demo");
    window.location.href = `mailto:info@lemonade.training?subject=${subject}`;
  }, []);

  const SecondaryIcon = secondary?.icon ?? IconVideo;
  const secondaryButton = (
    <motion.div {...buttonAnimation} className="sm:flex-1">
      <Button
        onClick={secondary?.onClick ?? handleRequestDemo}
        variant="outline"
        size="lg"
        className="w-full h-11 text-base font-semibold border-white/80 text-white bg-black hover:bg-zinc-900 hover:border-white"
      >
        {secondary?.label ?? "Request a Demo"}
        <SecondaryIcon className="ml-1 size-4" />
      </Button>
    </motion.div>
  );

  return (
    <motion.div variants={fadeInUp} className={cn("flex flex-col sm:flex-row gap-3", className)}>
      {isLoggedIn ? (
        <>
          {/* Same width as Create account, so the button keeps its place in the row */}
          <motion.div {...buttonAnimation} className="sm:w-[calc(50%-6px)]">
            <Button onClick={() => openDashboard()} size="lg" className={PRIMARY}>
              Go to Dashboard
              <IconArrowRight className="ml-1 size-4" />
            </Button>
          </motion.div>
          {secondaryWhenSignedIn && secondaryButton}
        </>
      ) : (
        <>
          <motion.div {...buttonAnimation} className="sm:flex-1">
            <Button onClick={handleSignUp} size="lg" className={PRIMARY}>
              Create account
              <IconArrowRight className="ml-1 size-4" />
            </Button>
          </motion.div>
          {secondaryButton}
        </>
      )}
    </motion.div>
  );
}
